import axios from '@Utils/httpService';
import { log, error } from '@/utils/debugLogger';

/**
 * Client for the M1 composite data-source engine.
 *
 * ROUTE: the controller is `[Route("api/data-sources")]` (DataSourcesController.cs:42).
 * ASP.NET matches literal segments case-insensitively but NOT hyphen-insensitively, so
 * `api/DataSources` is a 404 — the hyphenated spelling is the only one that reaches it.
 *
 * FACILITY IS AN ARGUMENT, NEVER AMBIENT. Every function takes an explicit facilityId and
 * sends it as X-Facility-Id. This workspace is a GLOBAL route, so `store.state.assetId` has
 * no life here (App.vue:38) — nothing under src/features/DataSources reads it. The selected
 * target is page state and travels down as a parameter.
 *
 * The header is REQUIRED by the whole controller, not merely honoured: every action reads
 * IFacilityContext and answers 400 "X-Facility-Id header is required for data sources." when
 * it is absent (DataSourcesController.cs:94-96 and the same guard at :111 :130 :150 :185
 * :229 :251 :277 :297 :313). VERIFIED ON THE WIRE 2026-08-18 against the local API — a
 * header-less list request answers 400, never an unscoped list. Omitting the header when the
 * caller has no target is therefore fail-closed rather than a silent widening.
 *
 * LISTING SCOPE is facility PLUS the group that facility belongs to
 * (DataSourceDefinitionService.cs:272-278), so a row returned for a facility target may be
 * owned by that facility's group — callers must read each row's own facilityId/facilityGroupId
 * rather than assume the selected target.
 */

const baseUrl = 'api/data-sources';

/**
 * @param {string} facilityId
 * @returns {{ 'X-Facility-Id'?: string }}
 */
function targetHeaders(facilityId) {
  return facilityId ? { 'X-Facility-Id': facilityId } : {};
}

/**
 * A live-row mutation without its read token is not a weaker write; it is an unsafe write.
 * Refuse it before the HTTP client can turn a malformed UI state into last-write-wins.
 * @param {string} facilityId
 * @param {string} rowVersion
 * @param {string} action
 * @returns {{ 'X-Facility-Id'?: string, 'If-Match': string }}
 */
function concurrencyHeaders(facilityId, rowVersion, action) {
  if (!rowVersion) {
    throw new Error(`Cannot ${action} composite source because its row version is missing.`);
  }
  return { ...targetHeaders(facilityId), 'If-Match': rowVersion };
}

export default {
  /**
   * Page of definitions reachable from `facilityId`.
   * @param {string} facilityId
   * @param {{ page?: number, itemsPerPage?: number, search?: string, status?: string }} [query]
   * @returns {Promise<{ data: { items: Array, total: number } }>}
   */
  list(facilityId, query = {}) {
    log('[compositeSourceService] list', { facilityId, query });
    return axios
      .get({ url: baseUrl, params: query, headers: targetHeaders(facilityId) })
      .catch((err) => {
        error('[compositeSourceService] list failed', { facilityId, err });
        throw err;
      });
  },

  /**
   * One definition by id, scoped to `facilityId`. Out-of-scope answers 404 by design.
   * @param {string} id
   * @param {string} facilityId
   */
  get(id, facilityId) {
    log('[compositeSourceService] get', { id, facilityId });
    return axios
      .get({ url: `${baseUrl}/${id}`, headers: targetHeaders(facilityId) })
      .catch((err) => {
        error('[compositeSourceService] get failed', { id, facilityId, err });
        throw err;
      });
  },

  /**
   * The join graph a definition may be built from — every traversal reachable from its root,
   * and the columns each reachable node can project. The wizard renders this and never
   * invents its own catalogue.
   * @param {string} id
   * @param {string} facilityId
   */
  graph(id, facilityId) {
    log('[compositeSourceService] graph', { id, facilityId });
    return axios
      .get({ url: `${baseUrl}/${id}/graph`, headers: targetHeaders(facilityId) })
      .catch((err) => {
        error('[compositeSourceService] graph failed', { id, facilityId, err });
        throw err;
      });
  },

  /**
   * The authoring graph for a local, unsaved root. Resolves schema only; it never creates or
   * updates a definition.
   * @param {{ rootKind: string, rootId: string }} root
   * @param {string} facilityId
   */
  graphForRoot(root, facilityId) {
    log('[compositeSourceService] graphForRoot', { root, facilityId });
    return axios
      .post({ url: `${baseUrl}/graph`, data: root, headers: targetHeaders(facilityId) })
      .catch((err) => {
        error('[compositeSourceService] graphForRoot failed', { root, facilityId, err });
        throw err;
      });
  },

  /**
   * Creates a draft. No SQL object is created — compiling happens at publish.
   * @param {{ name: string, facilityId?: string, facilityGroupId?: string, definitionJson: string }} definition
   * @param {string} facilityId
   */
  create(definition, facilityId) {
    log('[compositeSourceService] create', { name: definition && definition.name, facilityId });
    return axios
      .post({ url: baseUrl, data: definition, headers: targetHeaders(facilityId) })
      .catch((err) => {
        error('[compositeSourceService] create failed', { facilityId, err });
        throw err;
      });
  },

  /**
   * Edits a DRAFT. A published definition answers 409 — unpublish first.
   *
   * `rowVersion` is the base64 concurrency token the read returned; it travels as If-Match,
   * which the server prefers over the body field when both are present
   * (DataSourcesController.cs:154-158).
   * @param {string} id
   * @param {{ name: string, definitionJson: string, rowVersion?: string }} definition
   * @param {string} facilityId
   * @param {string} [rowVersion]
   */
  update(id, definition, facilityId, rowVersion) {
    log('[compositeSourceService] update', { id, facilityId, hasRowVersion: !!rowVersion });
    const headers = targetHeaders(facilityId);
    if (rowVersion) headers['If-Match'] = rowVersion;

    return axios
      .put({ url: `${baseUrl}/${id}`, data: definition, headers })
      .catch((err) => {
        error('[compositeSourceService] update failed', { id, facilityId, err });
        throw err;
      });
  },

  /**
   * Atomically applies a local candidate to a Published or Broken source while preserving the
   * current row and compiled view if validation, compatibility, concurrency or DDL fails.
   * @param {string} id
   * @param {{ name: string, definitionJson: string }} definition
   * @param {string} facilityId
   * @param {string} rowVersion
   */
  apply(id, definition, facilityId, rowVersion) {
    log('[compositeSourceService] apply', { id, facilityId, hasRowVersion: !!rowVersion });
    const headers = concurrencyHeaders(facilityId, rowVersion, 'apply');

    return axios
      .post({ url: `${baseUrl}/${id}/apply`, data: definition, headers })
      .catch((err) => {
        error('[compositeSourceService] apply failed', { id, facilityId, err });
        throw err;
      });
  },

  /**
   * Runs an UNSAVED definition and returns a sample of what it would produce. Stores nothing
   * and creates no view.
   * @param {string} definitionJson
   * @param {string} facilityId
   */
  preview(definitionJson, facilityId) {
    log('[compositeSourceService] preview', { facilityId });
    return axios
      .post({ url: `${baseUrl}/preview`, data: { definitionJson }, headers: targetHeaders(facilityId) })
      .catch((err) => {
        error('[compositeSourceService] preview failed', { facilityId, err });
        throw err;
      });
  },

  /**
   * Runs a STORED definition and returns a sample of what it would produce, pinned to that
   * definition's facility. Creates nothing — no view is required and none is made, so this is
   * safe to call on a Draft (DataSourcesController.cs:289-303).
   *
   * Retained for callers that deliberately want the stored version. The authoring wizard uses
   * `preview` so its unsaved local working copy is what the author sees.
   * @param {string} id
   * @param {string} facilityId
   * @returns {Promise<{ data: { columns: string[], rows: Array<Object>, rowCount: number,
   *   rowLimit: number, grain: string, compileMilliseconds: number, executeMilliseconds: number,
   *   totalMilliseconds: number } }>}
   */
  previewStored(id, facilityId) {
    log('[compositeSourceService] previewStored', { id, facilityId });
    return axios
      .post({ url: `${baseUrl}/${id}/preview`, headers: targetHeaders(facilityId) })
      .catch((err) => {
        error('[compositeSourceService] previewStored failed', { id, facilityId, err });
        throw err;
      });
  },

  /**
   * Compiles the definition and creates or replaces the view it owns — row update and DDL in
   * one transaction. Send the definition's rowVersion to be told about a concurrent edit
   * instead of publishing over it.
   * @param {string} id
   * @param {string} facilityId
   * @param {string} [rowVersion]
   */
  publish(id, facilityId, rowVersion) {
    log('[compositeSourceService] publish', { id, facilityId, hasRowVersion: !!rowVersion });
    const headers = targetHeaders(facilityId);
    if (rowVersion) headers['If-Match'] = rowVersion;

    return axios
      .post({ url: `${baseUrl}/${id}/publish`, headers })
      .catch((err) => {
        error('[compositeSourceService] publish failed', { id, facilityId, err });
        throw err;
      });
  },

  /**
   * Drops the compiled view and returns the definition to Draft, which is what unlocks editing
   * again — a published definition refuses PUT with 409 `definitionNotDraft`.
   *
   * Repeatable by design: a definition that never compiled is already unpublished and answers
   * success without touching the database (DataSourcesController.cs:241-261).
   * @param {string} id
   * @param {string} facilityId
   * @param {string} [rowVersion]
   */
  unpublish(id, facilityId, rowVersion) {
    log('[compositeSourceService] unpublish', { id, facilityId, hasRowVersion: !!rowVersion });
    const headers = targetHeaders(facilityId);
    if (rowVersion) headers['If-Match'] = rowVersion;

    return axios
      .post({ url: `${baseUrl}/${id}/unpublish`, headers })
      .catch((err) => {
        error('[compositeSourceService] unpublish failed', { id, facilityId, err });
        throw err;
      });
  },

  /**
   * Deletes the definition and its id-derived compiled view through the server's one
   * transactional path.
   * @param {string} id
   * @param {string} facilityId
   * @param {string} rowVersion
   */
  delete(id, facilityId, rowVersion) {
    log('[compositeSourceService] delete', { id, facilityId, hasRowVersion: !!rowVersion });
    const headers = concurrencyHeaders(facilityId, rowVersion, 'delete');

    return axios
      .delete({ url: `${baseUrl}/${id}`, headers })
      .catch((err) => {
        error('[compositeSourceService] delete failed', { id, facilityId, err });
        throw err;
      });
  },
};
